import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import { LineChart } from 'components/common/charts/lineChart/lineChart';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/CriticalAnalysisIntensity/criticalAnalysisIntensity.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Switch } from 'components/DesignSystem/Switch';
import { useScurveProvider } from 'components/widgets/SCurve/Scurve.provider';
import { useScurveLogic } from 'components/widgets/SCurve/useScurve.logic';

const { componentKey, id, endpoint } = widgetsConfigurations.criticalityWeightedSCurve;

export const CriticalityWeightedSCurve = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.criticalityWeightedSCurve.title,
    setIsPayloadReady,
}: IComponentProps) => {
    // Provider
    const { ui, api } = useScurveProvider({
        componentKey,
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.scurveGraph.payload,
        widgetEndpoint: endpoint,
    });

    // Logic
    const { logic } = useScurveLogic({
        ui,
        api,
        widgetViewMode,
        setIsPayloadReady,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    <div data-testid={'annotation-filter'} className={classes.annotationFilter}>
                        <Switch
                            onChange={(event) => logic.update({ isShowCriticalityAnnotation: event.target.checked })}
                            checked={logic.isShowCriticalityAnnotation}
                            label={logic.isShowCriticalityAnnotation ? 'Hide Annotations' : 'Show Annotations'}
                            labelPlacement={'start'}
                        />
                    </div>,
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.scurveGraph.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.scurveGraph.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                <LineChart
                    height={logic.chartHeight}
                    series={logic.series}
                    options={logic.options}
                    noData={
                        logic.noDataCondition ? (
                            <NoData>{logic.notApplicable ? 'Not applicable with applied filter' : null}</NoData>
                        ) : undefined
                    }
                    isFetching={api.scurveGraph.instance.isFetching}
                    className={classes.lineChart}
                />
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
